import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { homeFor, useAuth } from '../auth.jsx';
import { checkEmail, checkRequired } from '../utils/validate.js';
import AuthLayout from '../components/AuthLayout.jsx';
import Alert from '../components/ui/Alert.jsx';
import Field from '../components/Field.jsx';
import PasswordField from '../components/PasswordField.jsx';

const MIN_PASSWORD = 12;

export default function Register() {
  const auth = useAuth();
  const { user, register } = auth;
  const navigate = useNavigate();
  const location = useLocation();
  // Signing up from a team invite: the address is fixed to the one invited
  const invite = location.state?.invite || null;
  const [f, setF] = useState({ first_name: '', last_name: '', email: invite?.email || '', password: '', password_confirm: '' });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={homeFor(auth)} replace />;
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };

  // Same rules as the API, checked first so people see problems straight away
  const localErrors = () => {
    const e = {};
    const first = checkRequired(f.first_name, 'your first name', { max: 100 });
    const last = checkRequired(f.last_name, 'your last name', { max: 100 });
    const email = checkEmail(f.email, { label: 'your email address' });
    if (first) e.first_name = first;
    if (last) e.last_name = last;
    if (email) e.email = email;
    if (f.password.length < MIN_PASSWORD) e.password = `Use at least ${MIN_PASSWORD} characters.`;
    else if (f.password.length > 200) e.password = 'Use 200 characters or fewer.';
    else if (f.password !== f.password_confirm) e.password_confirm = 'The passwords don’t match.';
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    const local = localErrors();
    setErrors(local); setError('');
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      const res = await register(f);
      navigate('/verify-email', { state: { email: res.email, expires_at: res.expires_at, resend_after: res.resend_after } });
    } catch (err) {
      // Field problems go by their fields; the box at the top is for anything else
      setErrors(err.data?.fields || {});
      setError(err.data?.fields ? '' : err.message);
      setBusy(false);
    }
  };

  const confirmMatches = f.password_confirm && f.password === f.password_confirm;

  return (
    <AuthLayout onSubmit={submit} plain>
      <h1>{invite ? `Create your account to join ${invite.organisation_name}` : 'Create your account'}</h1>
      <p className="muted">{invite
        ? 'Once you’ve confirmed your email address, you’ll join the team straight away.'
        : 'Protect your attendees and exhibitors from people pretending to be you.'}</p>
      {error && <Alert tone="danger">{error}</Alert>}
      <div className="row-2">
        <Field label="First name" autoComplete="given-name" required maxLength={100} value={f.first_name} onChange={set('first_name')} error={errors.first_name} />
        <Field label="Last name" autoComplete="family-name" required maxLength={100} value={f.last_name} onChange={set('last_name')} error={errors.last_name} />
      </div>
      <Field label="Email address" type="email" autoComplete="email" autoCapitalize="none" required value={f.email} onChange={set('email')} readOnly={!!invite}
        onBlur={() => f.email.trim() && setErrors((x) => ({ ...x, email: checkEmail(f.email, { label: 'your email address' }) || undefined }))}
        error={errors.email} hint={!errors.email ? (invite
          ? 'The address you were invited on. We’ll send a 6-digit code to confirm it’s yours.'
          : 'We’ll send a 6-digit code to confirm it’s yours.') : undefined} />
      <PasswordField label="Password" autoComplete="new-password" required value={f.password} onChange={set('password')}
        maxLength={200} error={errors.password} hint={!errors.password ? `At least ${MIN_PASSWORD} characters. A short phrase is easiest to remember.` : undefined} />
      <PasswordField label="Confirm password" autoComplete="new-password" required value={f.password_confirm} onChange={set('password_confirm')}
        error={errors.password_confirm} hint={confirmMatches && !errors.password_confirm ? '✓ Passwords match' : undefined} />
      <button type="submit" className="btn btn-primary btn-block" disabled={busy}>{busy ? 'Creating your account…' : 'Create account'}</button>
      <p className="muted small">By creating an account you agree to our <Link to="/terms" target="_blank">terms</Link> and <Link to="/privacy" target="_blank">privacy policy</Link>.</p>
      <p className="auth-switch">Already have an account? <Link to="/login" state={invite ? { from: `/accept-invite/${invite.token}`, email: invite.email } : undefined}>Sign in</Link></p>
    </AuthLayout>
  );
}
